// frontend/src/pages/DeveloperInfo.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Code as CodeIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Category as CategoryIcon,
  Visibility as VisibilityIcon,
  Api
} from '@mui/icons-material';
import { useThemeSettings } from '../contexts/ThemeContext';
import { documentationApi } from '../services/apiClient';
import DocumentViewer from '../components/DocumentViewer';

const DeveloperInfo = () => {
  const { t } = useThemeSettings();
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentationList, setDocumentationList] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    fetchDocumentationList();
  }, []);

  const fetchDocumentationList = async () => {
    try {
      setLoading(true);
      const response = await documentationApi.getList();
      setDocumentationList(response.data.docs);
      setError(null);
    } catch (err) {
      console.error('Error fetching documentation list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDoc = (doc) => {
    setSelectedDoc(doc);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDoc(null);
  };

  // Group documentation by category
  const groupedDocs = documentationList.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {});

  // Category metadata
  const categoryMetadata = {
    setup: {
      title: 'Setup & Configuration',
      icon: '⚙️',
      description: 'Initial setup, configuration, and deployment guides',
      color: '#2196f3'
    },
    branding: {
      title: 'Branding & Customization',
      icon: '🎨',
      description: 'Customizing the look and feel of the platform',
      color: '#ff5722'
    },
    internationalization: {
      title: 'Internationalization (i18n)',
      icon: '🌍',
      description: 'Multi-language support and translation guides',
      color: '#4caf50'
    },
    security: {
      title: 'Authentication & Security',
      icon: '🔐',
      description: 'Security, authentication flows, and user management',
      color: '#f44336'
    },
    workflows: {
      title: 'Workflows & Automation',
      icon: '🤖',
      description: 'Automated processes and workflow management',
      color: '#9c27b0'
    },
    api: {
      title: 'API Documentation',
      icon: '🛠️',
      description: 'API reference and usage guides',
      color: '#3f51b5'
    },
    user: {
      title: 'User Documentation',
      icon: '📚',
      description: 'End-user guides and manuals',
      color: '#ff9800'
    }
  };

  return (
    <Box
    className="page-transition-enter"
    sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" className="slide-down" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon fontSize="large" color="primary" />
          {t ? t('DeveloperInfo.developer_documentation') : 'Developer Documentation'}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t ? t('DeveloperInfo.admin_only') : 'Admin Only'}</strong> - This section is only visible to administrators.
          </Typography>
        </Alert>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <div className="spinner" />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" className="slide-up" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Documentation Sections */}
      {!loading && !error && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t ? t('DeveloperInfo.documentation_categories') : 'Documentation Categories'}
          </Typography>

          {Object.entries(groupedDocs).map(([categoryId, docs]) => {
            const section = categoryMetadata[categoryId] || {
              title: categoryId,
              icon: '📄',
              description: 'Documentation',
              color: '#757575'
            };
            return (
          <Accordion
            key={categoryId}
            className="card-animated card-enter"
            expanded={expanded === categoryId}
            onChange={handleChange(categoryId)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${categoryId}-content`}
              id={`${categoryId}-header`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography fontSize="1.5rem">{section.icon}</Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {section.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {section.description}
                  </Typography>
                </Box>
                <Chip
                  label={`${docs.length} ${docs.length === 1 ? 'doc' : 'docs'}`}
                  size="small"
                  sx={{ bgcolor: section.color, color: 'white' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {docs.map((doc) => (
                  <Grid item xs={12} md={6} key={doc.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <DescriptionIcon color="action" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {doc.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {doc.description}
                        </Typography>
                        <Chip
                          label={doc.file}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDoc(doc)}
                        >
                          {t ? t('DeveloperInfo.view_doc') : 'View Documentation'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
            );
          })}
        </Box>
      )}

      {/* Document Viewer Dialog */}
      <DocumentViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        docId={selectedDoc?.id}
        docTitle={selectedDoc?.title}
      />
    </Box>
  );
};

export default DeveloperInfo;
